/*
 * dateTimeUtils.tsx
 * General utility of date and time related functions.
 */

import { t } from "i18next";
import i18n from "internationalization/i18n";

// Converts a 24 hour time value into 12-hour time (for example: '14:30' to '2:30 PM')
export const convert24HourTo12HourFormat = (time: string): string => {
    // Get the hours and minutes from the field
    const [hours, minutes] = time.split(':');

    // Convert backend time to AM/PM format
    let hour = parseInt(hours, 10);
    const period = hour < 12 ? 'AM' : 'PM';

    if (hour > 12) hour -= 12;
    if (hour === 0) hour = 12;

    // Return time in 12-hour format
    return `${hour}:${minutes} ${period}`;
};

// Convert 12-hour time format (AM/PM) to 24-hour format
export const convertTo24HourFormat = (value: string, is12HourFormat?: boolean): string => {
    if (!is12HourFormat) {
        return value;
    }

    const parts = value.split(' ');
    const timeParts = parts[0].split(':');
    let hour = parseInt(timeParts[0], 10);
    const minute = timeParts[1];
    
    if (parts[1] === "PM" && hour !== 12) {
        hour += 12;
    } else if (parts[1] === "AM" && hour === 12) {
        hour = 0;
    }
    
    return `${hour.toString().padStart(2, '0')}:${minute}`;
};

// Convert a timestring (e.g. '9:00' or '9:00 PM') into a date object to compare the time difference
export const convertTimeStringToDate = (timeString: string): Date => {
    // Get the time part and optionally the modifier
    const [time, modifier] = timeString.split(" ");

    // Split the hours and minutes
    let [hours, minutes] = time.split(":").map(Number);

    // If 12 hour format, equal it to 24 hours
    if (modifier) {
        // Voor AM/PM formaten
        if (modifier.toLowerCase() === "PM" && hours < 12) {
            hours += 12;
        }
        if (modifier.toLowerCase() === "AM" && hours === 12) {
            hours = 0;
        }
    }

    // Create a new date object
    const now = new Date();

    // Set the extracted time in the date object
    now.setHours(hours);
    now.setMinutes(minutes);
    now.setSeconds(0);
    now.setMilliseconds(0);

    return now;
}

// Combines a date input and a time input into a datetime string
export const combineDateAndTimeInputsToDatetimeString = (dateInput: string, timeInput: string): string => {
    // Extract date part from date input if it's a datetime string
    let datePart = dateInput;
    if (dateInput.includes('T')) {
        datePart = dateInput.split('T')[0];
    }

    // Extract time part from time input if it's a datetime string
    let timePart = timeInput;
    if (timeInput.includes('T')) {
        timePart = timeInput.split('T')[1];
    }

    // If time part does not contain seconds, add ':00'
    if (!timePart.includes(':')) {
        timePart += ':00';
    } else if (timePart.split(':').length === 2) {
        timePart += ':00';
    }

    // Combine date and time parts into an ISO datetime string
    return `${datePart}T${timePart}`;
}

// Formats a backend date string into a localized date with written out month (e.g.: September 11, 2024)
export const formatDateString = (dateString: string, userLocale: string): string => {

    // Split the data string in year, month and day
    const [year, month, day] = dateString.split('-').map(Number);

    // Create a new date object. Let the month start on 0
    const dateObj = new Date(year, month - 1, day);

    // Format the date based on the current user locale
    const formattedDate = new Intl.DateTimeFormat(userLocale, {
        day: 'numeric',
        month: 'long',
        year: 'numeric',
    }).format(dateObj);

    return formattedDate
};

// Formats a backend date string into a short localized date for chart axis ticks (e.g.: 11 Jul)
export const formatChartAxisDate = (dateString: string, userLocale: string): string => {

    // Split the data string in year, month and day
    const [year, month, day] = dateString.split('-').map(Number);

    // Create a new date object. Let the month start on 0
    const dateObj = new Date(year, month - 1, day);

    // Format the date based on the current user locale, short month, no year
    const formattedDate = new Intl.DateTimeFormat(userLocale, {
        day: 'numeric',
        month: 'short',
    }).format(dateObj);

    return formattedDate
};

// Generate ISO weeknumbers
export function generateWeekNumber(date: Date, weekStartsOnSunday?: boolean): number {
    let dateObject = new Date(date.getTime());
    dateObject.setHours(0, 0, 0, 0);
    
    // Adjust for nearest thursday in ISO week date rule, accounting for start of the week
    let dayOfWeek = dateObject.getDay();
    
    if (weekStartsOnSunday) {
        dateObject.setDate(dateObject.getDate() + 4 - (dayOfWeek || 7)); // Shift to nearest thursday
    } else {
        dateObject.setDate(dateObject.getDate() + 3 - (dayOfWeek + 6) % 7); // Shift to nearest thursday
    }
    
    // Set reference to january 4, always in the first ISO week
    let jan4 = new Date(dateObject.getFullYear(), 0, 4);
    let jan4DayOfWeek = jan4.getDay();
    
    // Adjust jan4 for the start of the week
    if (weekStartsOnSunday) {
        jan4DayOfWeek = (jan4DayOfWeek === 0) ? 7 : jan4DayOfWeek;
    } else {
        jan4DayOfWeek = (jan4DayOfWeek + 6) % 7; // monday = 0, sunday = 6
    }
    
    // Calculate days between the adjusted date and january 4
    let daysBetween = (dateObject.getTime() - jan4.getTime()) / 86400000;
    
    // Calculate the right ISO week number, considering week starts on sunday
    return Math.ceil((daysBetween + jan4DayOfWeek + 1) / 7);
};

// Validates the start and end date. Sets the start date as end date when only an end time is entered and no end date. In this way the end time is on the same day
export const startDateEndTime = (updatedData: any, data: any): { adjustedUpdatedData: any, errors: { endDate?: string, endTime?: string } } => {
    let errors = {
        endDate: '',
        endTime: ''
    };

    // Get the end datetime from the updated data
    const endDateTime = updatedData["end_datetime"];

    // Get the start datetime from the updated data or existing fetched server data
    const startDateTime = updatedData["start_datetime"] || data["start_datetime"];

    // Check if end_datetime starts with "T" followed by the time in HH:MM:SS format
    if (endDateTime && /^T(\d{2}:\d{2}:\d{2})/.test(endDateTime)) {
        const startDatePart = startDateTime.split('T')[0];
        updatedData["end_datetime"] = `${startDatePart}${endDateTime}`;
    }

    if (startDateTime && endDateTime) {
        const startDate = new Date(startDateTime);
        const endDate = new Date(endDateTime);
    
        // If both start date and end date are on the same date, but the end time is before the start time
        if (startDate.toDateString() === endDate.toDateString()) {
            if (endDate.getTime() < startDate.getTime()) {
                errors.endTime = "date_time.alert.end_time_before_start_time_message";
            }
        // If end date is earlier than start date
        } else if (endDate < startDate) {
            errors.endDate = "date_time.alert.end_date_before_start_date_message";
        }
    }

    return { adjustedUpdatedData: updatedData, errors };
};

// Formats the date for timeline items
export const formatTimelineDate = (inputDate: Date | string, whole_day: boolean) => {
    // Get the current date and time
    const now = new Date();

    // Create date objects for today, tomorrow and yesterday
    const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
    const tomorrow = new Date(now.getFullYear(), now.getMonth(), now.getDate() + 1);
    const yesterday = new Date(now.getFullYear(), now.getMonth(), now.getDate() - 1);

    // Convert the item's inputted date to a JavaScript date object
    const itemDate = new Date(inputDate);

    // Check if the item date corresponds to today, tomorrow or yesterday
    const isToday = itemDate >= today && itemDate < tomorrow;
    const isTomorrow = itemDate >= tomorrow && itemDate < new Date(now.getFullYear(), now.getMonth(), now.getDate() + 2);
    const isYesterday = itemDate >= yesterday && itemDate < today;

    // Append the year when the date falls outside the current year
    const yearString = itemDate.getFullYear() !== now.getFullYear() ? ` ${itemDate.getFullYear()}` : '';

    // Default date string format (for example, "12 January" or "12 January 2024")
    let dateString = `${itemDate.getDate()} ${itemDate.toLocaleString('default', { month: 'long' })}${yearString}`;

    // Update the date string if it's today, tomorrow or yesterday
    if (isToday) dateString = i18n.t('date_time.relative_days.today');
    else if (isTomorrow) dateString = i18n.t('date_time.relative_days.tomorrow');
    else if (isYesterday) dateString = i18n.t('date_time.relative_days.yesterday');

    // Create a time string if task is not for the whole day (for example 'at 15:05')
    const timeString = !whole_day && (itemDate.getHours() || itemDate.getMinutes()) 
        ? ` ${i18n.t('date_time.time.at')} ${itemDate.getHours()}:${itemDate.getMinutes() < 10 ? '0' : ''}${itemDate.getMinutes()}`
        : '';

    // Combine the date and time strings (for example: 'Today at 15:05' or '19 March at 9:00')
    return `${dateString}${timeString}`;
};

// Determine the classname of overdue tasks or tasks of today
export const determineTaskDateClass = (inputDate: Date | string, wholeDay: boolean, isChecked: boolean) => {

    // If the task is checked, set the class to done
    if (isChecked) {
        return 'task-done';
    }

    // Get the current date and time
    const now = new Date();

    // Convert the item's inputted date to a JavaScript date object
    const itemDate = new Date(inputDate);

    // Create date object for today and check if the item date corresponds to today
    const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
    const isToday = itemDate >= today && itemDate < new Date(now.getFullYear(), now.getMonth(), now.getDate() + 1);

    if ((!wholeDay && itemDate < now || (wholeDay && itemDate < today)) && !isChecked) {
        return 'overdue';
    } else if (isToday && !isChecked) {
        return 'today';
    } else {
        return '';
    }
};

// Format a date string into a date based on the user locale. Additionally with written month, for example: '26 januari 2026'
export const formatDate = (dateString: string, userLocale: string, writtenMonth?: 'writtenMonth') => {

    // Convert the date string to a date object
    const dateObject = new Date(dateString);

    // Format the date with written month based on the user locale
    const formattedDate = dateObject.toLocaleDateString(userLocale, { 
        day: 'numeric', 
        month: writtenMonth ? 'long' : 'numeric', 
        year: 'numeric' 
    });

    return formattedDate;
};

// Format a date string into a date and time based on the user locale
export const formatDateTime = (dateTimeString: string, userLocale: string, userTimezone?: string,): string => {

    // Convert the date string to a date object
    const dateObject = new Date(dateTimeString);

    // Format the date and time with written month based on the user locale
    return new Intl.DateTimeFormat(userLocale, {
        day: 'numeric',
        month: 'numeric',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
        timeZone: userTimezone,
    }).format(dateObject);
};


// Get the year/month/day of a date as seen in the given timezone, without any
// re-parsing round-trip (avoids double timezone conversion bugs)
const getDatePartsInTimezone = (date: Date, timeZone?: string) => {
    const parts = new Intl.DateTimeFormat('en-US', {
        year: 'numeric', month: 'numeric', day: 'numeric', timeZone,
    }).formatToParts(date);

    const getPart = (type: string) => parseInt(parts.find(part => part.type === type)?.value || '0', 10);

    return { year: getPart('year'), month: getPart('month'), day: getPart('day') };
};

// Formats a date the way an email inbox (e.g. Gmail) does: just the time when it's
// today, "8 jul" when it's earlier this year, or a full date when it's a previous year.
// Always converts from the backend's UTC value to the user's locale/timezone.
export const formatEmailListDate = (dateString: string, userLocale: string, userTimezone?: string): string => {
    if (!dateString) return '';

    const date = new Date(dateString);
    const now = new Date();

    const dateParts = getDatePartsInTimezone(date, userTimezone);
    const nowParts = getDatePartsInTimezone(now, userTimezone);

    const isToday = dateParts.year === nowParts.year && dateParts.month === nowParts.month && dateParts.day === nowParts.day;
    const isSameYear = dateParts.year === nowParts.year;

    if (isToday) {
        return new Intl.DateTimeFormat(userLocale, { hour: '2-digit', minute: '2-digit', timeZone: userTimezone }).format(date);
    } else if (isSameYear) {
        return new Intl.DateTimeFormat(userLocale, { day: 'numeric', month: 'short', timeZone: userTimezone }).format(date);
    } else {
        return new Intl.DateTimeFormat(userLocale, { day: '2-digit', month: '2-digit', year: 'numeric', timeZone: userTimezone }).format(date);
    }
};

// Get the ISO weeknumber based on the given date
export function getISOWeekNumber(date: Date): number {
    const dateCopy = new Date(date.getFullYear(), date.getMonth(), date.getDate());
    dateCopy.setDate(dateCopy.getDate() + 3 - (dateCopy.getDay() + 6) % 7);
    const yearStart = new Date(dateCopy.getFullYear(), 0, 4);
    return Math.ceil(((dateCopy.getTime() - yearStart.getTime()) / 86400000 - 3 + (yearStart.getDay() + 6) % 7) / 7) + 1;
}
// Determine the amount of remaining trial days based on the end date
export const getTrialRemainingDays = (endDate: string): number => {

    // Parse the end date into a local date at midnight
    const [year, month, day] = endDate.split('T')[0].split('-').map(Number);
    const endMidnight = new Date(year, (month || 1) - 1, day || 1);

    // Get todays date at local midnight
    const now = new Date();
    const todayMidnight = new Date(now.getFullYear(), now.getMonth(), now.getDate());

    // Calculate the amount of milliseconds in a day
    const dayMilliseconds = 1000 * 60 * 60 * 24;

    // Count the remaining calendar days, never below zero
    const remainingDays = Math.ceil((endMidnight.getTime() - todayMidnight.getTime()) / dayMilliseconds);

    return Math.max(remainingDays, 0);
};
